import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Edit2, XCircle, ChevronsRight, ChevronsLeft, Trash2, HelpCircle } from 'lucide-react';
import JsonFormatView from './JsonFormatView';
import { useLanguage } from '../i18n';

const STORAGE_KEY_TABS = 'json-tabs-data';
const MAX_TABS = 10;

/**
 * 从JSON内容中智能提取标签名称
 * @param {string} jsonStr - JSON字符串
 * @returns {string|null} - 提取的名称，失败返回null
 */
function extractTabName(jsonStr) {
  try {
    const obj = JSON.parse(jsonStr);

    // 优先级1: 检测常见的名称字段
    const nameFields = ['name', 'title', 'label', 'description', 'type', 'category'];
    for (const field of nameFields) {
      if (obj[field] && typeof obj[field] === 'string' && obj[field].length > 0 && obj[field].length < 30) {
        return obj[field];
      }
    }

    // 优先级2: 检测API响应特征 - 通过code和msg推测
    if (obj.code !== undefined || obj.msg !== undefined || obj.message !== undefined) {
      const msg = obj.msg || obj.message || '';
      if (msg && typeof msg === 'string' && msg.length < 20) {
        return `API: ${msg}`;
      }
      if (obj.code === 0 || obj.code === 200) {
        return 'API响应';
      }
    }

    // 优先级3: 检测数据字段
    if (obj.data) {
      if (Array.isArray(obj.data) && obj.data.length > 0) {
        const firstItem = obj.data[0];
        // 尝试从第一个数组元素中提取名称
        for (const field of nameFields) {
          if (firstItem[field] && typeof firstItem[field] === 'string' && firstItem[field].length < 30) {
            return firstItem[field];
          }
        }
        return `列表(${obj.data.length}项)`;
      }
    }

    // 优先级4: 检测常见业务对象
    if (obj.user || obj.users) return '用户数据';
    if (obj.order || obj.orders) return '订单数据';
    if (obj.product || obj.products) return '商品数据';
    if (obj.list || obj.items) return '列表数据';

    // 优先级5: 根据顶层键名推测
    const keys = Object.keys(obj);
    if (keys.length === 1) {
      return keys[0];
    }

    return null; // 无法提取有意义的名称
  } catch (e) {
    return null;
  }
}

/**
 * JSON 多标签页组件
 * 支持同时打开多个 JSON 实例，每个标签独立状态
 * @param {Object} theme - 主题配置
 */
function JsonTabsView({ theme }) {
  const { t, language } = useLanguage();

  // 标签页状态：[{ id, name, isEditing }]
  const [tabs, setTabs] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_TABS);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.length > 0 ? parsed : [{ id: Date.now(), name: 'Tab 1', isEditing: false }];
      }
    } catch (e) {
      console.error('Failed to load tabs:', e);
    }
    return [{ id: Date.now(), name: 'Tab 1', isEditing: false }];
  });

  const [activeTabId, setActiveTabId] = useState(() => tabs[0].id);
  const [editingTabId, setEditingTabId] = useState(null);
  const editInputRef = useRef(null);

  // 右键菜单状态
  const [contextMenu, setContextMenu] = useState(null); // { tabId, x, y }
  const contextMenuRef = useRef(null);

  // 处理JSON解析成功，自动更新标签名
  const handleJsonParsed = (tabId, jsonStr) => {
    const extractedName = extractTabName(jsonStr);
    if (extractedName) {
      setTabs(tabs => tabs.map(tab =>
        tab.id === tabId ? { ...tab, name: extractedName } : tab
      ));
    }
  };

  // 右键菜单功能
  const handleContextMenu = (e, tabId) => {
    e.preventDefault();
    setContextMenu({
      tabId,
      x: e.clientX,
      y: e.clientY
    });
  };

  // 关闭其他标签
  const handleCloseOtherTabs = (targetTabId) => {
    const count = tabs.length - 1;
    if (count > 0 && count > 2) {
      if (!window.confirm(t('json.tabs.confirmCloseOthers', { count }))) {
        return;
      }
    }

    const newTabs = tabs.filter(t => t.id === targetTabId);
    setTabs(newTabs);
    setActiveTabId(targetTabId);
    setContextMenu(null);

    // 清理被关闭标签的 localStorage
    tabs.forEach(tab => {
      if (tab.id !== targetTabId) {
        localStorage.removeItem(`json-input-${tab.id}`);
        localStorage.removeItem(`json-tree-${tab.id}`);
      }
    });
  };

  // 关闭右侧标签
  const handleCloseRightTabs = (targetTabId) => {
    const targetIndex = tabs.findIndex(t => t.id === targetTabId);
    const rightCount = tabs.length - targetIndex - 1;

    if (rightCount === 0) {
      return; // 没有右侧标签
    }

    if (rightCount > 2) {
      if (!window.confirm(t('json.tabs.confirmCloseRight', { count: rightCount }))) {
        return;
      }
    }

    const newTabs = tabs.slice(0, targetIndex + 1);
    setTabs(newTabs);
    setContextMenu(null);

    // 清理被关闭标签的 localStorage
    for (let i = targetIndex + 1; i < tabs.length; i++) {
      localStorage.removeItem(`json-input-${tabs[i].id}`);
      localStorage.removeItem(`json-tree-${tabs[i].id}`);
    }

    // 如果当前激活的标签被关闭，切换到目标标签
    if (!newTabs.find(t => t.id === activeTabId)) {
      setActiveTabId(targetTabId);
    }
  };

  // 关闭左侧标签
  const handleCloseLeftTabs = (targetTabId) => {
    const targetIndex = tabs.findIndex(t => t.id === targetTabId);
    const leftCount = targetIndex;

    if (leftCount === 0) {
      return; // 没有左侧标签
    }

    if (leftCount > 2) {
      if (!window.confirm(t('json.tabs.confirmCloseLeft', { count: leftCount }))) {
        return;
      }
    }

    const newTabs = tabs.slice(targetIndex);
    setTabs(newTabs);
    setContextMenu(null);

    // 清理被关闭标签的 localStorage
    for (let i = 0; i < targetIndex; i++) {
      localStorage.removeItem(`json-input-${tabs[i].id}`);
      localStorage.removeItem(`json-tree-${tabs[i].id}`);
    }

    // 如果当前激活的标签被关闭，切换到目标标签
    if (!newTabs.find(t => t.id === activeTabId)) {
      setActiveTabId(targetTabId);
    }
  };

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  // 持久化标签页信息（不包含内容，内容由各自的 JsonFormatView 管理）
  useEffect(() => {
    const tabsToSave = tabs.map(tab => ({ id: tab.id, name: tab.name }));
    localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(tabsToSave));
  }, [tabs]);

  // 新建标签
  const handleNewTab = () => {
    if (tabs.length >= MAX_TABS) {
      alert(t('json.tabs.maxTabsAlert', { max: MAX_TABS }));
      return;
    }

    const newTab = {
      id: Date.now(),
      name: t('json.tabs.defaultName', { num: tabs.length + 1 }),
      isEditing: false
    };

    setTabs([...tabs, newTab]);
    setActiveTabId(newTab.id);
  };

  // 关闭标签
  const handleCloseTab = (tabId, e) => {
    e.stopPropagation(); // 防止触发标签切换

    if (tabs.length === 1) {
      // 最后一个标签，清空内容但不删除
      // 由于内容是 JsonFormatView 自己管理的，这里只需要保留标签
      return;
    }

    const tabIndex = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);

    // 如果关闭的是当前标签，切换到相邻标签
    if (activeTabId === tabId) {
      const newActiveIndex = Math.min(tabIndex, newTabs.length - 1);
      setActiveTabId(newTabs[newActiveIndex].id);
    }

    // 清理该标签的 localStorage 数据
    // 注意：JsonFormatView 使用的 key 需要加上 tabId 前缀
    localStorage.removeItem(`json-input-${tabId}`);
    localStorage.removeItem(`json-tree-${tabId}`);
  };

  // 切换标签
  const handleTabClick = (tabId) => {
    if (editingTabId === tabId) return; // 编辑模式下不切换
    setActiveTabId(tabId);
  };

  // 双击开始重命名
  const handleTabDoubleClick = (tabId, e) => {
    e.stopPropagation();
    setEditingTabId(tabId);
  };

  // 完成重命名
  const handleRenameComplete = (tabId, newName) => {
    if (newName.trim()) {
      setTabs(tabs.map(tab =>
        tab.id === tabId ? { ...tab, name: newName.trim() } : tab
      ));
    }
    setEditingTabId(null);
  };

  // 重命名输入框自动聚焦
  useEffect(() => {
    if (editingTabId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTabId]);

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      <div className="px-8 py-6">
        {/* 标签栏卡片 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl px-4 mb-4 shadow-md">
          <div className="flex items-center justify-between gap-1 h-12">
          {/* 标签列表 + 新建按钮 */}
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 flex-1">
            {tabs.map((tab, index) => {
              const isActive = activeTabId === tab.id;
              const isEditing = editingTabId === tab.id;

              return (
                <div
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  onDoubleClick={(e) => handleTabDoubleClick(tab.id, e)}
                  onContextMenu={(e) => handleContextMenu(e, tab.id)}
                  onMouseDown={(e) => {
                    // 中键点击关闭标签（浏览器标准行为）
                    if (e.button === 1 && tabs.length > 1) {
                      e.preventDefault();
                      handleCloseTab(tab.id, e);
                    }
                  }}
                  className={`
                    group relative flex items-center gap-2 px-3 h-8 rounded-md cursor-pointer
                    transition-all duration-150 min-w-[120px] max-w-[200px] flex-shrink-0
                    ${isActive
                      ? `${theme?.activeTab || 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'} border`
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }
                  `}
                >
                  {/* 状态指示点 */}
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    isActive ? 'bg-blue-500' : 'bg-gray-400 dark:bg-gray-500'
                  }`} />

                  {/* 标签名称或编辑输入框 */}
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      defaultValue={tab.name}
                      className="flex-1 bg-white dark:bg-gray-800 border border-blue-400 rounded px-1 py-0.5 text-xs focus:outline-none"
                      onBlur={(e) => handleRenameComplete(tab.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameComplete(tab.id, e.target.value);
                        } else if (e.key === 'Escape') {
                          setEditingTabId(null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="flex-1 text-xs font-medium truncate">
                      {tab.name}
                    </span>
                  )}

                  {/* 关闭按钮 - hover 时显示 */}
                  {tabs.length > 1 && (
                    <button
                      onClick={(e) => handleCloseTab(tab.id, e)}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-0.5 transition-opacity"
                      title={t('json.tabs.closeTab')}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}

            {/* 新建标签按钮 - 紧跟在标签后面 */}
            <button
              onClick={handleNewTab}
              disabled={tabs.length >= MAX_TABS}
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ml-1"
              title={`${t('json.tabs.newTab')} (${tabs.length}/${MAX_TABS})`}
            >
              <Plus size={16} />
            </button>
          </div>

          {/* 帮助入口 */}
          <Link
            to="/help/json"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title={language === 'zh' ? '使用指南' : 'Guide'}
          >
            <HelpCircle size={16} />
            <span className="hidden sm:inline">{language === 'zh' ? '使用指南' : 'Guide'}</span>
          </Link>
          </div>
        </div>

        {/* 标签内容区 */}
        <div>
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={activeTabId === tab.id ? '' : 'hidden'}
          >
            {/* 每个标签都是独立的 JsonFormatView 实例 */}
            <JsonFormatView
              tabId={tab.id}
              theme={theme}
              onJsonParsed={(jsonStr) => handleJsonParsed(tab.id, jsonStr)}
            />
          </div>
        ))}
      </div>

      {/* 底部功能说明卡片 */}
      <div className="mt-6 space-y-4">
        {/* 功能说明 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-lg p-5">
          <h3 className="text-base font-semibold text-blue-900 dark:text-blue-300 mb-3">
            {t('json.features.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800 dark:text-blue-300">
            <div>
              <p className="mb-2">
                <strong>{language === 'zh' ? 'Postman风格表格视图' : 'Table View (Postman Style)'}</strong>
                <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded">{language === 'zh' ? '推荐' : 'Popular'}</span>
              </p>
              <p className="text-blue-700/80 dark:text-blue-400/80 text-xs">
                {language === 'zh'
                  ? '将JSON数据以表格形式展示，支持键/值/类型三列展示。类似Postman的预览方式，可展开嵌套对象和数组。适合查看API响应数据。'
                  : 'Display JSON data as HTML table with Key/Value/Type columns. Similar to Postman preview. Perfect for viewing API responses and nested data structures.'}
              </p>
            </div>
            <div>
              <p className="mb-2"><strong>{t('json.features.innerJson.title')}</strong></p>
              <p className="text-blue-700/80 dark:text-blue-400/80 text-xs">
                {t('json.features.innerJson.desc')}
              </p>
            </div>
            <div>
              <p className="mb-2"><strong>{t('json.features.nodeDetail.title')}</strong></p>
              <p className="text-blue-700/80 dark:text-blue-400/80 text-xs">
                {t('json.features.nodeDetail.desc')}
              </p>
            </div>
            <div>
              <p className="mb-2"><strong>{t('json.features.multiTab.title')}</strong></p>
              <p className="text-blue-700/80 dark:text-blue-400/80 text-xs">
                {t('json.features.multiTab.desc')}
              </p>
            </div>
          </div>
        </div>

        {/* 使用技巧 */}
        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 rounded-lg p-5">
          <h3 className="text-base font-semibold text-green-900 dark:text-green-300 mb-3">
            {t('json.features.tips')}
          </h3>
          <div className="space-y-2 text-sm text-green-800 dark:text-green-300">
            <p>• <strong>{t('json.features.historyRecord.title')}</strong>: {t('json.features.historyRecord.desc')}</p>
            <p>• <strong>{t('json.features.smartFix.title')}</strong>: {t('json.features.smartFix.desc')}</p>
            <p>• <strong>{t('json.features.doubleClickRename.title')}</strong>: {t('json.features.doubleClickRename.desc')}</p>
            <p>• <strong>{t('json.features.middleClickClose.title')}</strong>: {t('json.features.middleClickClose.desc')}</p>
          </div>
        </div>
      </div>

      {/* 右键菜单 */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600 py-1 min-w-[200px] z-50"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
        >
          {/* 重命名 */}
          <button
            onClick={() => {
              setEditingTabId(contextMenu.tabId);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-300 transition-colors"
          >
            <Edit2 size={14} />
            <span>{t('json.tabs.rename')}</span>
          </button>

          <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

          {/* 关闭标签 */}
          <button
            onClick={() => {
              handleCloseTab(contextMenu.tabId, new Event('click'));
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors"
          >
            <XCircle size={14} />
            <span>{t('json.tabs.closeTab')}</span>
          </button>

          {/* 关闭其他标签 */}
          {tabs.length > 1 && (
            <button
              onClick={() => handleCloseOtherTabs(contextMenu.tabId)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors"
            >
              <Trash2 size={14} />
              <span>{t('json.tabs.closeOthers')}</span>
            </button>
          )}

          {/* 关闭右侧标签 */}
          {tabs.findIndex(tab => tab.id === contextMenu.tabId) < tabs.length - 1 && (
            <button
              onClick={() => handleCloseRightTabs(contextMenu.tabId)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors"
            >
              <ChevronsRight size={14} />
              <span>{t('json.tabs.closeRight')}</span>
            </button>
          )}

          {/* 关闭左侧标签 */}
          {tabs.findIndex(tab => tab.id === contextMenu.tabId) > 0 && (
            <button
              onClick={() => handleCloseLeftTabs(contextMenu.tabId)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors"
            >
              <ChevronsLeft size={14} />
              <span>{t('json.tabs.closeLeft')}</span>
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default JsonTabsView;
